import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/axios';
import { Building2, Search, Plus, MapPin, Calendar, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

// Simple localStorage persistence hook
const useLocalStorageState = (key, defaultValue) => {
  const [state, setState] = useState(() => {
    try {
      const item = window.localStorage.getItem(`companies_${key}`);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key "companies_${key}":`, error);
      return defaultValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      setState(value);
      window.localStorage.setItem(`companies_${key}`, JSON.stringify(value));
    } catch (error) {
      console.error(`Error setting localStorage key "companies_${key}":`, error);
    }
  }, [key]);

  return [state, setValue];
};

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useLocalStorageState('search', '');
  const [selectedRegion, setSelectedRegion] = useLocalStorageState('region', 'all');
  const [selectedStatus, setSelectedStatus] = useLocalStorageState('status', 'all');
  const [currentPage, setCurrentPage] = useLocalStorageState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;
  
  // Search term for API calls (only updated when search button is clicked)
  const [apiSearchTerm, setApiSearchTerm] = useState(searchTerm);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (apiSearchTerm) {
        params.append('search', apiSearchTerm);
      }
      
      if (selectedRegion !== 'all') {
        params.append('region', selectedRegion);
      }
      
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }

      const response = await api.get(`/api/companies?${params}`);
      setCompanies(response.data.companies || []);
      setTotalPages(Math.ceil(response.data.pagination?.total / itemsPerPage) || 1);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedRegion, selectedStatus, apiSearchTerm, itemsPerPage]);

  // Initialize API search term from localStorage on component mount
  useEffect(() => {
    setApiSearchTerm(searchTerm);
  }, []); // Only run once on mount

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // Update API search term to trigger search
    setApiSearchTerm(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setApiSearchTerm('');
    setCurrentPage(1);
    // fetchCompanies will be called automatically by useEffect when apiSearchTerm and currentPage change
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setApiSearchTerm('');
    setSelectedRegion('all');
    setSelectedStatus('all');
    setCurrentPage(1);
    // fetchCompanies will be called automatically by useEffect when all state changes
  };

  const handleRegionChange = (value) => {
    setSelectedRegion(value);
    setCurrentPage(1); // Reset to page 1 when changing filters
    // fetchCompanies will be called automatically by useEffect when selectedRegion and currentPage change
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setCurrentPage(1); // Reset to page 1 when changing filters
    // fetchCompanies will be called automatically by useEffect when selectedStatus and currentPage change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // fetchCompanies will be called automatically by useEffect when currentPage changes
  };

  const getCompanyTypeBadge = (type) => {
    const typeColors = {
      company: 'badge-primary',
      supplier: 'badge-success',
      customer: 'badge-warning'
    };
    return <span className={`badge ${typeColors[type] || 'badge-secondary'}`}>{type}</span>;
  };

  const openCompanyUrl = (url) => {
    if (url) {
      const fullUrl = url.startsWith('http') ? url : `https://www.importyeti.com/${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Companies</h1>
        <p className="page-subtitle">Manage your company contacts and relationships</p>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10 w-full"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={loading}
              >
                Search
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="btn btn-outline px-4"
                  disabled={loading}
                >
                  Clear
                </button>
              )}
            </div>
          </form>
          
          <div className="min-w-[150px]">
            <select
              value={selectedRegion}
              onChange={(e) => handleRegionChange(e.target.value)}
              className="form-select"
            >
              <option value="all">All Regions</option>
              <option value="Palm Coast">Palm Coast</option>
              <option value="Orlando">Orlando</option>
            </select>
          </div>
          
          <div className="min-w-[150px]">
            <select
              value={selectedStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="form-select"
            >
              <option value="all">All Statuses</option>
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Hot Lead">Hot Lead</option>
              <option value="In Discussions">In Discussions</option>
              <option value="Customer">Customer</option>
              <option value="Decision-maker Not Found">Decision-maker Not Found</option>
              <option value="Dead Lead">Dead Lead</option>
              <option value="Contacts Populated">Contacts Populated</option>
              <option value="Out Of Area">Out Of Area</option>
            </select>
          </div>
        </div>
        
        <div className="flex gap-2">
          {(searchTerm || selectedRegion !== 'all' || selectedStatus !== 'all') && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Active filters:</span>
              {searchTerm && <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">Search: "{searchTerm}"</span>}
              {selectedRegion !== 'all' && <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">Region: {selectedRegion}</span>}
              {selectedStatus !== 'all' && <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Status: {selectedStatus}</span>}
            </div>
          )}
          <button
            onClick={handleClearAllFilters}
            className="btn btn-outline"
            disabled={loading || (searchTerm === '' && selectedRegion === 'all' && selectedStatus === 'all')}
          >
            Clear All Filters
          </button>
          <Link to="/import" className="btn btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Import Companies
          </Link>
        </div>
      </div>

      {/* Companies List */}
      <div className="card">
        <div className="card-body">
          {companies.length > 0 ? (
            <div className="space-y-4">
              {companies.map((company) => (
                <div key={company.id} className="flex items-center justify-between p-4 bg-white rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm">
                  <div className="flex items-center flex-1">
                    <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{company.name}</h3>
                        {getCompanyTypeBadge(company.type)}
                        {company.status && (
                          <span className={`badge ${
                            company.status === 'Customer' ? 'badge-success' :
                            company.status === 'Hot Lead' ? 'badge-warning' :
                            company.status === 'In Discussions' ? 'badge-primary' :
                            company.status === 'Contacted' ? 'badge-warning' :
                            company.status === 'Decision-maker Not Found' ? 'badge-secondary' :
                            company.status === 'New' ? 'badge-secondary' :
                            company.status === 'Contacts Populated' ? 'badge-primary' :
                            company.status === 'Out Of Area' ? 'badge-secondary' :
                            company.status === 'Dead Lead' ? 'badge-danger' :
                            'badge-secondary'
                          }`}>
                            {company.status}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 space-x-4">
                        {company.region && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                            {company.region}
                          </span>
                        )}
                        {company.country_code && (
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {company.country_code}&nbsp;                                                                                    
                          </div>
                        )}
                        {company.total_shipments > 0 && (
                          <span>{company.total_shipments} shipments</span>
                        )}
                        {company.most_recent_shipment && (
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(company.most_recent_shipment).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      {company.address && (
                        <p className="text-sm text-gray-600 mt-1 truncate max-w-md">
                          {company.address}
                        </p>
                      )}
                      {company.url && (
                        <button
                          onClick={() => openCompanyUrl(company.url)}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors mt-1"
                          title="View on ImportYeti"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          <span className="text-sm">View on ImportYeti</span>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/companies/${company.id}`}
                      className="btn btn-blue btn-sm"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'Try adjusting your search terms' : 'Get started by importing your first company'}
              </p>
              <Link to="/import" className="btn btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Import Companies
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn btn-outline btn-sm"
          >
            Previous
          </button>
          
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn btn-outline btn-sm"
          >
            Next
          </button>
          
          {currentPage > 1 && (
            <button
              onClick={() => handlePageChange(1)}
              className="btn btn-outline btn-sm"
            >
              First Page
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Companies;
